import { create } from 'zustand';
import { Subscription, OrganizationMember } from '../types';

interface Invite {
  id: string;
  githubUsername?: string;
  email?: string;
}

interface ApiToken {
  id: string;
  name: string;
  tokenPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

interface SettingsState {
  subscription: Subscription | null;
  members: OrganizationMember[];
  invites: Invite[];
  loadingSubscription: boolean;
  currentUserRole: string | null;
  apiTokens: ApiToken[];
  loadingTokens: boolean;
  currentUsername: string | null;
  hasLoadedOnce: boolean;

  // Actions
  setCurrentUsername: (username: string | null) => void;
  loadSubscriptionData: () => Promise<void>;
  loadApiTokens: () => Promise<void>;
  removeMember: (memberId: number) => Promise<void>;
  cancelInvite: (inviteId: string) => Promise<void>;
  inviteMember: (githubUsername: string) => Promise<{
    success: boolean;
    error?: string;
    inviteUrl?: string;
    invite?: Invite;
    requiresUpgrade?: boolean;
  }>;
  revokeApiToken: (tokenId: string) => Promise<boolean>;
  createApiToken: (tokenData: {
    name: string;
    expiresIn: string;
    scopes: string[];
  }) => Promise<{ success: boolean; token?: string; error?: string }>;
  checkSubscriptionStatus: () => Promise<{ success: boolean; tier?: string; error?: string }>;
}

export const useSettingsStore = create<SettingsState>((set, get) => ({
  subscription: null,
  members: [],
  invites: [],
  loadingSubscription: true,
  currentUserRole: null,
  apiTokens: [],
  loadingTokens: false,
  currentUsername: null,
  hasLoadedOnce: false,

  setCurrentUsername: username => {
    set({ currentUsername: username });
  },

  loadSubscriptionData: async () => {
    // Only load if we haven't loaded yet or if explicitly called
    try {
      set({ loadingSubscription: true });

      const fetchOptions = { credentials: 'same-origin' as RequestCredentials };
      const [subResponse, membersResponse, invitesResponse] = await Promise.all([
        fetch('/api/organizations/subscription', fetchOptions),
        fetch('/api/organizations/members', fetchOptions),
        fetch('/api/organizations/invites', fetchOptions),
      ]);

      if (
        subResponse.status === 401 ||
        membersResponse.status === 401 ||
        invitesResponse.status === 401 ||
        subResponse.status === 400 ||
        membersResponse.status === 400 ||
        invitesResponse.status === 400
      ) {
        return;
      }

      const [subData, membersData, invitesData] = await Promise.all([
        subResponse.json(),
        membersResponse.json(),
        invitesResponse.json(),
      ]);

      const currentUsername = get().currentUsername;
      const currentUser = membersData.members?.find(
        (m: OrganizationMember) => m.githubUsername === currentUsername
      );

      set({
        subscription: subData.subscription,
        members: membersData.members || [],
        invites: invitesData.invites || [],
        currentUserRole: currentUser?.role || null,
        hasLoadedOnce: true,
      });
    } catch (error: any) {
      if (
        !error.message?.includes('401') &&
        !error.message?.includes('Unauthorized') &&
        error.name !== 'AbortError'
      ) {
        console.error('Failed to load subscription data:', error);
      }
    } finally {
      set({ loadingSubscription: false });
    }
  },

  loadApiTokens: async () => {
    set({ loadingTokens: true });
    try {
      const response = await fetch('/api/v1/tokens', {
        credentials: 'same-origin',
      });
      if (response.status === 401 || response.status === 400) {
        return;
      }
      if (response.ok) {
        const data = await response.json();
        set({ apiTokens: data.tokens || [] });
      }
    } catch (error) {
      console.error('Failed to load API tokens:', error);
    } finally {
      set({ loadingTokens: false });
    }
  },

  removeMember: async (memberId: number) => {
    await fetch(`/api/organizations/members/${memberId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });

    set(state => ({
      members: state.members.filter(m => m.githubUserId !== memberId),
    }));
  },

  cancelInvite: async (inviteId: string) => {
    await fetch(`/api/organizations/invites/${inviteId}`, {
      method: 'DELETE',
      credentials: 'same-origin',
    });

    set(state => ({
      invites: state.invites.filter(i => i.id !== inviteId),
    }));
  },

  inviteMember: async (githubUsername: string) => {
    try {
      const response = await fetch('/api/organizations/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ githubUsername }),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        set(state => ({
          invites: [...state.invites, data.invite],
        }));
        return { success: true, inviteUrl: data.inviteUrl, invite: data.invite };
      } else if (data.requiresUpgrade) {
        return { success: false, error: data.error, requiresUpgrade: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Failed to send invite' };
    }
  },

  revokeApiToken: async (tokenId: string) => {
    try {
      const response = await fetch(`/api/v1/tokens/${tokenId}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      });

      if (response.ok) {
        set(state => ({
          apiTokens: state.apiTokens.filter(t => t.id !== tokenId),
        }));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  },

  createApiToken: async tokenData => {
    try {
      const response = await fetch('/api/v1/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(tokenData),
      });

      const data = await response.json();
      if (response.ok) {
        await get().loadApiTokens();
        return { success: true, token: data.token };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: 'Failed to create API token' };
    }
  },

  checkSubscriptionStatus: async () => {
    try {
      const syncResponse = await fetch('/api/organizations/subscription/sync', {
        method: 'POST',
        credentials: 'same-origin',
      });
      const syncData = await syncResponse.json();

      if (syncData.success && syncData.tier === 'PRO') {
        await get().loadSubscriptionData();
        return { success: true, tier: 'PRO' };
      } else {
        const response = await fetch('/api/organizations/subscription', {
          credentials: 'same-origin',
        });
        const data = await response.json();
        set({ subscription: data.subscription });

        if (data.subscription?.tier === 'PRO') {
          return { success: true, tier: 'PRO' };
        } else {
          return { success: false, error: 'Subscription still processing' };
        }
      }
    } catch (error) {
      return { success: false, error: 'Failed to check subscription status' };
    }
  },
}));
